import { z } from "zod";
import type {
  CreateExpertLevelRequest,
  ExpertLevel,
  UpdateExpertLevelRequest,
} from "@shopexperts/legacy-api";

/**
 * View-model the form binds to. The API stores cents and basis points, but
 * humans speak dollars and percents — we convert on submit so the Laravel
 * payload stays integer-precise while the form is friendly.
 */
export interface ExpertLevelFormValues {
  public_title: string;
  public_description: string;
  internal_notes: string;
  threshold_dollars: number;
  commission_percent: number;
  is_active: boolean;
  sort_order: number;
}

const requiredNumber = <T extends z.ZodType<number>>(schema: T) =>
  z.preprocess(
    (value) => {
      if (value === "") return undefined;
      return typeof value === "string" ? Number(value) : value;
    },
    schema,
  );

export const expertLevelFormSchema = z.object({
  public_title: z
    .string()
    .trim()
    .min(1, "Title is required")
    .max(120, "Keep the title under 120 characters"),
  public_description: z
    .string()
    .trim()
    .max(500, "Keep the description under 500 characters"),
  internal_notes: z
    .string()
    .trim()
    .max(2000, "Keep internal notes under 2000 characters"),
  threshold_dollars: requiredNumber(
    z
      .number({ message: "Threshold is required" })
      .nonnegative("Threshold can't be negative")
      .max(10_000_000, "Threshold looks too large"),
  ),
  commission_percent: requiredNumber(
    z
      .number({ message: "Commission is required" })
      .min(0, "Commission can't be negative")
      .max(100, "Commission can't exceed 100%"),
  ),
  is_active: z.boolean(),
  sort_order: requiredNumber(
    z
      .number({ message: "Order is required" })
      .int("Order must be a whole number")
      .min(0, "Order can't be negative"),
  ),
});

export type ExpertLevelFormSchema = typeof expertLevelFormSchema;

export function defaultExpertLevelFormValues(): ExpertLevelFormValues {
  return {
    public_title: "",
    public_description: "",
    internal_notes: "",
    threshold_dollars: 0,
    commission_percent: 0,
    is_active: true,
    sort_order: 0,
  };
}

export function expertLevelToFormValues(level: ExpertLevel): ExpertLevelFormValues {
  return {
    public_title: level.public_title ?? "",
    public_description: level.public_description ?? "",
    internal_notes: level.internal_notes ?? "",
    threshold_dollars: centsToDollars(level.auto_enroll_threshold_gross_cents),
    commission_percent: bpsToPercent(level.commission_rate_bps),
    is_active: level.is_active,
    sort_order: level.sort_order ?? 0,
  };
}

export function formValuesToCreatePayload(values: ExpertLevelFormValues): CreateExpertLevelRequest {
  const description = values.public_description.trim();
  const notes = values.internal_notes.trim();
  return {
    public_title: values.public_title.trim(),
    public_description: description.length > 0 ? description : null,
    internal_notes: notes.length > 0 ? notes : null,
    auto_enroll_threshold_gross_cents: dollarsToCents(values.threshold_dollars),
    commission_rate_bps: percentToBps(values.commission_percent),
    is_active: values.is_active,
    sort_order: values.sort_order,
  };
}

export function formValuesToUpdatePayload(values: ExpertLevelFormValues): UpdateExpertLevelRequest {
  return formValuesToCreatePayload(values);
}

export function centsToDollars(cents: number): number {
  if (!Number.isFinite(cents)) return 0;
  return Math.round(cents) / 100;
}

export function dollarsToCents(dollars: number): number {
  if (!Number.isFinite(dollars)) return 0;
  return Math.round(dollars * 100);
}

/**
 * Convert basis points to a human percent. 10000 bps = 100%. Rounded to 2
 * decimals to avoid floating-point artifacts in the input (e.g. 7.5%).
 */
export function bpsToPercent(bps: number): number {
  if (!Number.isFinite(bps)) return 0;
  return Math.round(bps) / 100;
}

export function percentToBps(percent: number): number {
  if (!Number.isFinite(percent)) return 0;
  return Math.round(percent * 100);
}

const thresholdFormatter = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

export function formatThreshold(cents: number): string {
  return thresholdFormatter.format(centsToDollars(cents));
}

const percentFormatter = new Intl.NumberFormat("en-US", {
  style: "percent",
  minimumFractionDigits: 0,
  maximumFractionDigits: 2,
});

export function formatCommission(bps: number): string {
  return percentFormatter.format(bpsToPercent(bps) / 100);
}
